import * as React from "react"

import { AppShell } from "@/components/chat/app-shell"
import { Alert, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { AuthError } from "@/chat/auth"
import {
  isLogin,
  isPassword,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
} from "@/chat/protocol"
import { useChat, type SignInMode } from "@/chat/store"

/**
 * Sign-in and registration on one screen. An account is a login and a
 * password, nothing else — the login is the name everyone sees.
 */
export function Join() {
  const { signIn, sessionNotice } = useChat()
  const [mode, setMode] = React.useState<SignInMode>("login")
  const [login, setLogin] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const trimmedLogin = login.trim()
  const valid = isLogin(trimmedLogin) && isPassword(password)

  // The server's rules, surfaced before a round trip rather than after.
  const hint =
    mode === "register"
      ? trimmedLogin && !isLogin(trimmedLogin)
        ? "Логин: 3–32 символа, латиница, цифры и подчёркивание."
        : password && !isPassword(password)
          ? `Пароль: от ${PASSWORD_MIN_LENGTH} до ${PASSWORD_MAX_LENGTH} символов.`
          : null
      : null

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!valid || busy) return

    setBusy(true)
    setError(null)

    try {
      await signIn(mode, trimmedLogin, password)
    } catch (caught) {
      setError(
        caught instanceof AuthError
          ? caught.message
          : "Что-то пошло не так. Попробуйте ещё раз."
      )
      setBusy(false)
    }
  }

  function switchMode() {
    setMode((m) => (m === "login" ? "register" : "login"))
    setError(null)
  }

  const notice = error ?? sessionNotice

  return (
    <AppShell>
      <div className="flex flex-1 flex-col justify-center px-6 pb-16">
        <h1 className="text-3xl font-semibold tracking-tight">ChatForge</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {mode === "login"
            ? "Войдите, чтобы продолжить. Логин — это имя, которое видят остальные."
            : "Придумайте логин и пароль. Логин станет вашим именем в чате; почта не нужна."}
        </p>

        {notice && (
          <Alert variant="destructive" className="mt-6">
            <AlertTitle>{notice}</AlertTitle>
          </Alert>
        )}

        <form onSubmit={submit} className="mt-8 flex flex-col gap-3">
          <Label htmlFor="login">Логин</Label>
          <Input
            id="login"
            value={login}
            onChange={(event) => setLogin(event.target.value)}
            maxLength={32}
            autoFocus
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="Nickname"
          />

          <Label htmlFor="password" className="mt-2">
            Пароль
          </Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            maxLength={PASSWORD_MAX_LENGTH}
            autoComplete={
              mode === "login" ? "current-password" : "new-password"
            }
            placeholder="••••••••"
          />

          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}

          <Button type="submit" disabled={!valid || busy} className="mt-2">
            {busy && <Spinner className="size-4" />}
            {mode === "login" ? "Войти" : "Создать аккаунт"}
          </Button>
        </form>

        <button
          type="button"
          onClick={switchMode}
          className="mt-6 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          {mode === "login"
            ? "Нет аккаунта? Зарегистрироваться"
            : "Уже есть аккаунт? Войти"}
        </button>
      </div>
    </AppShell>
  )
}
